import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { ArrowUpRight, Menu, X } from "lucide-react";

const IMAGES = {
  earth:
    "https://images.unsplash.com/photo-1634176866089-b633f4aec882?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxlYXJ0aCUyMG5pZ2h0JTIwc3BhY2V8ZW58MHx8fHwxNzkyNDEzMDYwfDA&ixlib=rb-4.1.0&q=85",
  marble:
    "https://images.unsplash.com/photo-1786673211400-90f79d15b1ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbWFyYmxlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzkyNDEzMDYwfDA&ixlib=rb-4.1.0&q=85",
};

const INSTITUTIONS = [
  {
    number: "01",
    title: "A-Square Group",
    desc: "Enterprise · Technology",
    link: "https://asquare.group",
  },
  {
    number: "02",
    title: "Arora Corporation",
    desc: "Stewardship · Capital",
    link: "https://aroracorporation.com/",
  },
  {
    number: "03",
    title: "Arora Foundation",
    desc: "Legacy · Impact",
    link: "https://arora.org.in/",
  },
];

const NEWS = [
  {
    title: "Intelligence for Government",
    link: "https://www.openpr.com/news/4625469/ankesh-arora-wants-to-build-the-intelligence-layer",
  },
  {
    title: "Strategic Priorities 2026-27",
    link: "https://www.openpr.com/news/4392790/a-square-group-outlines-2026-27-investment-strategy-in-the-age",
  },
];

const NAV_LINKS = [
  { label: "Mandate", href: "#mandate" },
  { label: "Institutions", href: "#institutions" },
  { label: "Newsroom", href: "#newsroom" },
  { label: "Correspondence", href: "#correspondence" },
];

const Reveal = ({ children, className = "", ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`scroll-reveal ${className}`} {...props}>
      {children}
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 80 && y > lastY.current) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-6 md:px-12 lg:px-16 mix-blend-difference transition-transform duration-500 ${
          hidden && !open ? "-translate-y-full md:translate-y-0" : "translate-y-0"
        }`}
      >
        <a
          data-testid="header-wordmark"
          href="#top"
          className="text-[11px] font-medium tracking-[0.3em] text-[#F5F5F0]/70 uppercase"
        >
          Arora
        </a>
        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              data-testid={`nav-${link.label.toLowerCase()}-link`}
              href={link.href}
              className="text-[11px] font-medium tracking-[0.3em] uppercase text-[#F5F5F0]/70 hover:text-[#C5A880] transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="md:hidden text-[#F5F5F0]"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>
      {open && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-40 bg-[#050505]/97 backdrop-blur-md flex flex-col items-center justify-center gap-8 md:hidden"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
              href={link.href}
              onClick={() => setOpen(false)}
              className="text-[11px] font-medium tracking-[0.3em] uppercase text-[#F5F5F0]/70 hover:text-[#C5A880] transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </>
  );
};

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={IMAGES.earth}
        alt="Earth at night from space"
        data-testid="hero-earth-image"
        className="w-full h-full object-cover opacity-70"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/75 to-[#050505]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
    </div>
    <div className="relative z-10 px-6 md:px-12 lg:px-16 w-full max-w-7xl mx-auto">
      <h1
        data-testid="hero-headline"
        className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[1.05] text-[#F5F5F0]"
      >
        <span className="reveal reveal-delay-2 block">
          A Global Perspective.
        </span>
        <span className="reveal reveal-delay-3 block text-[#C5A880] italic">
          A Generational Horizon.
        </span>
      </h1>
      <div className="reveal reveal-delay-4 mt-12 flex items-center gap-6">
        <div className="h-px w-16 bg-[#C5A880]/40" />
        <p className="text-[11px] font-medium tracking-[0.3em] uppercase text-[#F5F5F0]/60">
          New York · New Delhi · Dubai
        </p>
      </div>
    </div>
  </section>
);

const Mandate = () => (
  <section
    id="mandate"
    data-testid="mandate-section"
    className="relative pt-[20px] pb-[76px] md:pt-[52px] md:pb-[106px] px-6 md:px-12 lg:px-16"
  >
    <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
      <Reveal className="md:col-span-4">
        <p
          data-testid="mandate-eyebrow"
          className="text-[11px] font-medium tracking-[0.5em] uppercase text-[#C5A880]"
        >
          Mandate
        </p>
      </Reveal>
      <Reveal className="md:col-span-8">
        <h2
          data-testid="mandate-title"
          className="font-display text-3xl md:text-5xl text-[#F5F5F0] mb-4"
        >
          GOVXI
        </h2>
        <p
          data-testid="mandate-subtitle"
          className="font-display italic text-xl md:text-2xl text-[#C5A880] mb-10"
        >
          Governance × Intelligence
        </p>
        <p
          data-testid="mandate-body"
          className="text-sm md:text-base font-light leading-loose tracking-wide text-[#F5F5F0]/60 max-w-2xl"
        >
          Advancing intelligent governance systems for governments, public
          institutions and critical infrastructure. Guided by institutional
          longevity, applied intelligence, and long-horizon stewardship.
        </p>
        <p
          data-testid="mandate-attribution"
          className="mt-10 text-xs font-light tracking-[0.25em] uppercase text-[#F5F5F0]/40"
        >
          Architect — Ankesh Arora
        </p>
      </Reveal>
    </div>
  </section>
);

const Institutions = () => (
  <section
    id="institutions"
    data-testid="institutions-section"
    className="relative pt-[58px] pb-[58px] md:pt-[86px] md:pb-[86px] px-6 md:px-12 lg:px-16"
  >
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p
          data-testid="institutions-eyebrow"
          className="text-[11px] font-medium tracking-[0.5em] uppercase text-[#C5A880] mb-16"
        >
          Institutions
        </p>
      </Reveal>
      <div className="border-t hairline">
        {INSTITUTIONS.map((inst) => (
          <Reveal key={inst.number}>
            <a
              data-testid={`institution-${inst.number}-link`}
              href={inst.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inst-row group flex items-center justify-between gap-6 py-10 md:py-14 px-2 md:px-6 border-b hairline"
            >
              <div className="flex items-baseline md:items-center gap-8 md:gap-16">
                <span
                  data-testid={`institution-${inst.number}-number`}
                  className="font-display text-2xl md:text-3xl text-[#C5A880]/70"
                >
                  {inst.number}
                </span>
                <div>
                  <h3
                    data-testid={`institution-${inst.number}-title`}
                    className="inst-title font-display text-xl md:text-4xl text-[#F5F5F0] uppercase tracking-wide"
                  >
                    {inst.title}
                  </h3>
                  <p
                    data-testid={`institution-${inst.number}-desc`}
                    className="mt-3 text-[10px] md:text-xs font-light tracking-[0.35em] uppercase text-[#F5F5F0]/45"
                  >
                    {inst.desc}
                  </p>
                </div>
              </div>
              <ArrowUpRight
                data-testid={`institution-${inst.number}-arrow`}
                className="inst-arrow shrink-0 text-[#F5F5F0]/50"
                size={28}
              />
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Newsroom = () => (
  <section
    id="newsroom"
    data-testid="newsroom-section"
    className="relative pt-[58px] pb-[58px] md:pt-[86px] md:pb-[86px] px-6 md:px-12 lg:px-16"
  >
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p
          data-testid="newsroom-eyebrow"
          className="text-[11px] font-medium tracking-[0.5em] uppercase text-[#C5A880] mb-16"
        >
          Newsroom
        </p>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6">
        {NEWS.map((item, i) => (
          <Reveal key={item.title}>
            <a
              data-testid={`news-${i + 1}-card`}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="news-card group flex flex-col justify-between min-h-[100px] md:min-h-[120px] p-8 md:p-10 border hairline"
            >
              <div className="flex items-start justify-between">
                <span className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#F5F5F0]/40">
                  Press
                </span>
                <ArrowUpRight
                  data-testid={`news-${i + 1}-arrow`}
                  className="news-arrow text-[#C5A880]"
                  size={24}
                />
              </div>
              <h3
                data-testid={`news-${i + 1}-title`}
                className="font-display text-xl md:text-2xl leading-tight text-[#F5F5F0]"
              >
                {item.title}
              </h3>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Correspondence = () => (
  <section
    id="correspondence"
    data-testid="correspondence-section"
    className="relative pt-[76px] pb-32 md:pt-[116px] md:pb-48 px-6 md:px-12 lg:px-16 overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={IMAGES.marble}
        alt="Dark marble architecture"
        data-testid="correspondence-marble-image"
        className="w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-[#050505]/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
    </div>
    <div className="relative z-10 max-w-7xl mx-auto">
      <Reveal>
        <p
          data-testid="correspondence-eyebrow"
          className="text-[11px] font-medium tracking-[0.5em] uppercase text-[#C5A880] mb-12"
        >
          Correspondence
        </p>
      </Reveal>
      <Reveal>
        <h2
          data-testid="correspondence-title"
          className="font-display text-3xl md:text-5xl leading-[1.1] text-[#F5F5F0] max-w-4xl"
        >
          The Office of
          <br />
          Ankesh Arora
        </h2>
      </Reveal>
      <Reveal>
        <p
          data-testid="correspondence-locations"
          className="mt-10 text-[11px] font-medium tracking-[0.3em] uppercase text-[#F5F5F0]/60"
        >
          New York · New Delhi · Dubai
        </p>
      </Reveal>
      <Reveal>
        <a
          data-testid="correspondence-email-link"
          href="mailto:ankesh@arora.global"
          className="email-link mt-12 inline-flex items-center gap-3 font-display text-base md:text-xl text-[#C5A880]"
        >
          ankesh@arora.global
          <ArrowUpRight size={20} data-testid="correspondence-email-arrow" />
        </a>
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="border-t hairline px-6 md:px-12 lg:px-16 py-10"
  >
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <span
        data-testid="footer-wordmark"
        className="text-[10px] font-light tracking-[0.3em] uppercase text-[#F5F5F0]/40"
      >
        Arora
      </span>
      <span
        data-testid="footer-locations"
        className="hidden md:block text-[10px] font-light tracking-[0.3em] uppercase text-[#F5F5F0]/40"
      >
        New York · New Delhi · Dubai
      </span>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App">
      <Header />
      <main>
        <Hero />
        <Mandate />
        <Institutions />
        <Newsroom />
        <Correspondence />
      </main>
      <Footer />
    </div>
  );
}

export default App;
